import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import {
  deleteTeam as deleteTeamAction,
  getTournamentTeams,
  getTournamentTeamsByFilter,
  reset,
  selectError,
  selectFilter,
  selectLoading,
  selectTeams,
  selectTeamsByFilter,
  selectTeamsPagination,
  selectTournamentId,
  setFilter,
} from 'modules/teams/store/teams';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';

export const useTeamList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userTournamentId = useSelector(authStoreUserTournamentId);
  const { info } = useOrganizer();

  // Пагинацией и perPage управляет CategoryListPage через локальные query/storage
  const loading = useSelector(selectLoading);
  const { allDataLoaded, allItemsCount, limit } = useSelector(selectTeamsPagination(1));
  const teams = useSelector(selectTeams);
  const teamsByFilter = useSelector(selectTeamsByFilter);
  const filter = useSelector(selectFilter);
  const tournamentId = useSelector(selectTournamentId);
  const error = useSelector(selectError);

  const loadNextPages = useCallback(() => {
    if (allDataLoaded) return;
    dispatch(
      getTournamentTeams({
        offset: allItemsCount,
        limit,
        sportType: info?.sportType ?? SportType.football,
      }),
    );
  }, [allDataLoaded, allItemsCount, dispatch, info?.sportType, limit]);

  const deleteTeam = useCallback(
    (id: number) => {
      return dispatch(deleteTeamAction({ id })).unwrap();
    },
    [dispatch],
  );

  const changeFilter = useCallback(
    (value: string) => {
      if (value === filter.text) return;
      dispatch(setFilter({ text: value }));
      if (!info) return;

      dispatch(
        getTournamentTeamsByFilter({
          namePart: value,
          limit,
          sportType: info?.sportType ?? SportType.football,
        }),
      );
    },
    [dispatch, filter?.text, info, limit],
  );

  useEffect(() => {
    try {
      if (!loading.isLoading && !loading.isLoaded && !error) {
        dispatch(getTournamentTeams({ offset: allItemsCount, limit, sportType: info?.sportType ?? SportType.football })).unwrap();
      }
    } catch (e) {
      toast.error('Не удалось получить команды');
      console.error(e);
    }
  }, [allDataLoaded, allItemsCount, dispatch, error, info?.sportType, limit, loading.isLoaded, loading.isLoading]);

  useEffect(() => {
    if (!userTournamentId) {
      console.warn('User tournament ID is not defined');
      return;
    }

    if (!tournamentId) {
      return;
    }

    if (tournamentId !== userTournamentId) {
      dispatch(reset({ tournamentId: userTournamentId }));
      dispatch(getTournamentTeams({ offset: 0, limit, sportType: info?.sportType ?? SportType.football }));
    }
  }, [dispatch, tournamentId, userTournamentId, limit, info?.sportType]);

  return {
    isLoading: loading.isLoading || filter.isLoading,
    isLoaded: loading.isLoaded,
    teams: teams ?? [],
    loadNextPages,
    changeFilter,
    deleteTeam,
    teamsByFilter: filter.text ? teamsByFilter : null,
  };
};
